/**
 * trendModel — чистая логика вьюхи «Динамика» во вкладке «Реклама по уровням»
 * ==============================================================================
 *
 * Сервер (/api/ads/levels/trend) отдаёт точки по периодам регистрации для
 * одного среза (уровень N или день жизни D). Здесь — только то, что зависит от
 * переключателей виджета: строки графиков, подписи периодов и среза.
 */

import { adsLevels as adsLevelsSpecs } from '@ggstat/chart-specs';

export const MODE_LEVEL = 'level';
export const MODE_DAY = 'day';

export const VALUE_PER_PLAYER = 'perPlayer';
export const VALUE_SHARE = 'share';

export const GROUP_BY_OPTIONS = adsLevelsSpecs.trend.groupByOptions;

/** Цвет группы плейсментов — по id, чтобы при смене среза цвета не переезжали. */
const GROUP_COLORS = adsLevelsSpecs.trend.groupColors;

export function groupColor(id) {
  return Object.prototype.hasOwnProperty.call(GROUP_COLORS, id) ? GROUP_COLORS[id] : GROUP_COLORS.other;
}

/** Ключ серии группы в строке графика: id групп — константы сервера, но префикс исключает коллизию с полями точки. */
export const groupSeriesKey = (id) => `g_${id}`;

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/**
 * Подпись периода регистрации: день/неделя — «дд.мм» (с годом «дд.мм.гг»,
 * если окно захватывает несколько лет), месяц — «сен 2026».
 */
export function formatPeriod(period, groupBy, withYear = false) {
  if (!period || !/^\d{4}-\d{2}-\d{2}$/.test(period)) return String(period ?? '');
  const [y, m, d] = period.split('-');
  if (groupBy === 'month') return `${MONTHS[Number(m) - 1]} ${y}`;
  return withYear ? `${d}.${m}.${y.slice(2)}` : `${d}.${m}`;
}

/** Что на графике значит «игроки» в текущем срезе. */
export function playersLabel(mode, target) {
  return mode === MODE_DAY ? `Живы в D${target}` : `Дошли до ур. ${target}`;
}

/**
 * Строки графиков: одна на период, с метриками точки и сериями групп
 * плейсментов (на игрока или доля игроков — по valueMode).
 */
export function buildTrendRows(points, groups, groupBy, valueMode = VALUE_PER_PLAYER) {
  const years = new Set((points || []).map((p) => String(p.period ?? '').slice(0, 4)));
  const withYear = years.size > 1;
  return (points || []).map((p) => {
    // Период без игроков среза — пропуск на линии и «—» в таблице, а не ложный ноль.
    const ratio = (value) => (p.players > 0 ? value ?? 0 : null);
    const row = {
      period: p.period,
      label: `${formatPeriod(p.period, groupBy, withYear)}${p.partial ? '*' : ''}`,
      partial: !!p.partial,
      cohort: p.cohort,
      players: p.players,
      playersShare: p.playersShare,
      adsPerPlayer: ratio(p.total?.perPlayer),
      adsPerReg: p.cohort > 0 ? p.adsPerReg ?? 0 : null,
      watchedShare: ratio(p.total?.share)
    };
    for (const g of groups || []) {
      const cell = p.groups?.[g.id];
      row[groupSeriesKey(g.id)] = ratio(cell ? cell[valueMode] : 0);
    }
    return row;
  });
}

/** Есть ли что рисовать: хотя бы в одном периоде есть игроки среза. */
export function hasTrendData(points) {
  return (points || []).some((p) => p.players > 0);
}

const pctFormat = (v) => (v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`);
const ratioFormat = (v) => (v == null ? '—' : Number(v).toFixed(2));

export function formatTrendValue(value, valueMode) {
  return valueMode === VALUE_SHARE ? pctFormat(value) : ratioFormat(value);
}

export { pctFormat, ratioFormat };
